import type { Post } from "@/lib/news/types";
import hero from "@/assets/news/billan-bast-i-test-200000.jpg";

const BILLAN_HUVUD = [
  "lendo", "sambla", "enklare", "advisa", "axo",
  "loansse", "finlo", "ekonomen", "enkelfinans", "northmillsamla", "thorn", "banky",
];

const NAST_BAST = ["moneezy", "klaralan", "brixopl", "northmill", "merax"];

export const BILLAN_BAST_I_TEST: Record<string, Post> = {
  "billan-bast-i-test-200000-kr-utan-kontantinsats": {
    title: "Billån bäst i test 2026 – 200 000 kr utan kontantinsats",
    intro:
      "Har du hittat bilen men saknar kontantinsatsen? Du är långt ifrån ensam, och det behöver inte betyda att bilköpet stannar. Här jämför vi billån på 200 000 kr utan kontantinsats 2026 – ränta, avgifter, löptid och krav – och visar vilka långivare som blev bäst i test. Vi räknar också öppet på vad ett billån utan kontantinsats faktiskt kostar jämfört med ett lån där du lägger 20 procent kontant, så att du kan välja med siffrorna framför dig.",
    author: "Erik Lindqvist",
    authorTitle: "Ekonomiredaktör",
    published: "2026-09-01",
    updated: "2026-09-01",
    heroSrc: hero,
    heroAlt: "Par tar emot bilnycklar hos bilhandlare efter billån på 200 000 kr utan kontantinsats",
    topLenderId: "lendo",
    bottomLenderId: "sambla",
    topLenderTitle: "Bäst i test – billån 200 000 kr utan kontantinsats",
    bottomLenderTitle: "Vår rekommendation för flera bud på en kreditupplysning",
    reviewsLenderId: "sambla",
    cta: { to: "/billan", label: "Jämför billån och se dina bud" },
    calculator: {
      title: "Räkna på billån 200 000 kr",
      intro:
        "Fyll i belopp, löptid och ränta så ser du månadskostnad, räntekostnad, total lånekostnad och effektiv ränta för ditt billån.",
      note:
        "Månadskostnaden på ett billån styrs av lånebelopp, löptid och nominell ränta, men den effektiva räntan visar din verkliga kostnad eftersom uppläggningsavgift och aviavgift räknas in. På 200 000 kr betyder en procentenhet i ränta flera tusen kronor över löptiden – räkna därför alltid på total lånekostnad, inte bara månadskostnad.",
      amount: 200000,
      minAmount: 10000,
      maxAmount: 600000,
      months: 84,
      maxMonths: 144,
      rate: 8.5,
      minRate: 3,
      maxRate: 25,
      setupFee: 495,
      invoiceFee: 29,
    },
    faq: [
      {
        q: "Går det verkligen att få billån utan kontantinsats?",
        a: "Ja, men inte som traditionell bilfinansiering hos handlaren – där krävs normalt 20 procent kontant. Lösningen är i stället ett blancolån på hela beloppet. Du blir kontantköpare, bilen står inte som säkerhet och någon kontantinsats behövs inte.",
      },
      {
        q: "Vad kostar ett billån på 200 000 kr per månad?",
        a: "Med 8,5 procent nominell ränta och 84 månaders löptid landar månadskostnaden på cirka 3 160 kronor inklusive aviavgift. Total lånekostnad blir då ungefär 65 000 kronor. Med 6 procent sjunker månadskostnaden till cirka 2 950 kronor.",
      },
      {
        q: "Är blancolån dyrare än bilfinansiering med säkerhet?",
        a: "Räntan är oftast något högre eftersom långivaren saknar säkerhet. Samtidigt blir du kontantköpare och kan förhandla ned bilpriset, vilket i praktiken ofta äter upp hela ränteskillnaden.",
      },
      {
        q: "Hur många kreditupplysningar tas om jag söker via förmedlare?",
        a: "En enda. Förmedlaren skickar din ansökan vidare till flera banker på samma upplysning, och du får sedan jämföra buden utan att binda dig.",
      },
      {
        q: "Vilken inkomst krävs för att låna 200 000 kr?",
        a: "De flesta banker vill se en årsinkomst från cirka 200 000–240 000 kronor och en anställning som inte är provanställning. Bedömningen görs alltid mot din totala ekonomi, inklusive boendekostnad och befintliga lån.",
      },
      {
        q: "Kan jag få billån med betalningsanmärkning?",
        a: "Det är betydligt svårare på 200 000 kronor. Några långivare gör en individuell bedömning om anmärkningen är äldre och reglerad, särskilt med medsökande, men räntan blir då högre.",
      },
      {
        q: "Hur lång löptid bör jag välja på ett billån?",
        a: "Kortast möjliga som din budget klarar, och gärna inte längre än bilens förväntade livslängd. En löptid på 10 år för en bil du behåller i 5 år betyder att du fortsätter betala långt efter att bilen är såld.",
      },
      {
        q: "Kan jag lösa billånet i förtid?",
        a: "Ja, du har alltid rätt att lösa ett blancolån i förtid utan extra avgift. Räntan räknas på kvarvarande skuld, så varje extra inbetalning sänker totalkostnaden direkt.",
      },
    ],
    blocks: [
      { t: "h2", text: "Billån 200 000 kr utan kontantinsats – kort svar" },
      { t: "p", text: "Vill du låna 200 000 kronor till bilen utan att lägga en krona kontant är svaret ett blancolån, inte bilhandlarens finansiering. Blancolånet saknar säkerhet, kräver därför ingen kontantinsats och gör dig till kontantköpare hos säljaren." },
      { t: "p", text: "Vi förstår att bilköpet ofta är stressigt. Bilen finns i dag, avbetalningserbjudandet ligger på bordet och pappren ska skrivas på direkt. Därför lägger vi räntorna, avgifterna och totalkostnaden öppet – innan du bestämmer dig." },
      {
        t: "stats",
        items: [
          { label: "Typisk ränta 2026", value: "5 – 14 %" },
          { label: "Månadskostnad 200 000 kr / 7 år", value: "ca 3 160 kr" },
          { label: "Kreditupplysningar hos förmedlare", value: "1 st" },
        ],
      },

      { t: "h2", text: "Bäst i test – billån 2026" },
      { t: "p", text: "Vår ranking väger effektiv ränta tyngst, följt av antal anslutna banker, tydlighet i villkoren, tillstånd hos Finansinspektionen och verkliga kundomdömen. Ingen långivare kan köpa sig till en placering." },
      { t: "lenders", ids: BILLAN_HUVUD, title: "Alla långivare för billån 200 000 kr", note: "Räntan sätts individuellt efter kreditprövning. Räkneexemplen är typexempel enligt konsumentkreditlagen." },

      { t: "h2", text: "Näst bästa alternativen" },
      { t: "p", text: "Behöver du ett mindre belopp, eller får du avslag hos de stora bankerna, kan följande alternativ fungera. Räntan är ofta högre men kraven mildare." },
      { t: "lenders", ids: NAST_BAST, title: "Kompletterande långivare för bilköp" },

      { t: "h2", text: "Blancolån eller bilfinansiering med säkerhet?" },
      { t: "p", text: "Det här är den enskilt viktigaste frågan när kontantinsatsen saknas. De två alternativen ser lika ut i annonsen men fungerar helt olika i praktiken." },
      { t: "h3", text: "Blancolån – inget krav på kontantinsats" },
      { t: "p", text: "Du lånar hela beloppet utan säkerhet, äger bilen från dag ett och kan sälja den när du vill. Räntan är något högre, men du slipper både kontantinsats och restvärdesrisk." },
      { t: "h3", text: "Bilfinansiering med bilen som säkerhet" },
      { t: "p", text: "Räntan är lägre, men bilen står som pant, kontantinsats på 20 procent krävs oftast och du får inte sälja bilen fritt. Läs mer på vår sida om [billån](/billan)." },
      {
        t: "table",
        caption: "Jämförelse: 200 000 kr i bilköp",
        head: ["", "Blancolån", "Bilfinansiering med säkerhet"],
        rows: [
          ["Kontantinsats", "0 kr", "ca 40 000 kr"],
          ["Typisk ränta", "6 – 12 %", "4 – 8 %"],
          ["Äger bilen", "Ja, direkt", "Nej, bilen är pant"],
          ["Kan förhandla som kontantköpare", "Ja", "Nej"],
        ],
      },

      { t: "h2", text: "Vad kostar ett billån på 200 000 kr?" },
      { t: "p", text: "Kostnaden består av nominell ränta, uppläggningsavgift och aviavgift. På ett så här stort belopp är räntan helt avgörande – avgifterna påverkar bara marginellt." },
      {
        t: "table",
        caption: "Annuitetslån 200 000 kr, 84 månader, 495 kr uppläggning, 29 kr avi",
        head: ["Ränta", "Månadskostnad", "Räntekostnad", "Total kostnad"],
        rows: [
          ["5,00 %", "2 855 kr", "35 838 kr", "238 769 kr"],
          ["8,50 %", "3 160 kr", "61 508 kr", "264 439 kr"],
          ["12,00 %", "3 481 kr", "88 496 kr", "291 427 kr"],
          ["16,00 %", "3 866 kr", "120 826 kr", "323 757 kr"],
        ],
      },
      { t: "p", text: "Skillnaden mellan 5 och 12 procent är över 52 000 kronor på samma bil. Det är därför det lönar sig att jämföra bud i stället för att ta det första erbjudandet. Testa dina egna siffror i vår [lånekalkyl](/guider/lanekalkyl)." },

      { t: "h2", text: "Så påverkar löptiden totalkostnaden" },
      { t: "p", text: "Lång löptid ger låg månadskostnad men hög totalkostnad. Tumregeln är enkel: låt aldrig lånet leva längre än bilen." },
      {
        t: "table",
        caption: "200 000 kr till 8,5 % ränta",
        head: ["Löptid", "Månadskostnad", "Total kostnad"],
        rows: [
          ["5 år", "4 132 kr", "248 415 kr"],
          ["7 år", "3 160 kr", "264 439 kr"],
          ["10 år", "2 508 kr", "301 455 kr"],
          ["12 år", "2 251 kr", "324 549 kr"],
        ],
      },

      { t: "h2", text: "Krav för billån utan kontantinsats" },
      {
        t: "ul",
        items: [
          "Minst 18 år, ofta 20 år för belopp över 100 000 kronor.",
          "Årsinkomst från cirka 200 000 kronor.",
          "Fast anställning eller motsvarande stabil inkomst.",
          "Inga aktiva betalningsanmärkningar hos de flesta banker.",
          "Svenskt personnummer och mobilt BankID.",
        ],
      },
      { t: "h3", text: "Om du har betalningsanmärkning" },
      { t: "p", text: "På 200 000 kronor är kraven hårdare än på små krediter. Vår sida om [lån med betalningsanmärkning](/lan-med-betalningsanmarkning-bast-i-test-30000-kr) går igenom vilka som faktiskt prövar dig och vad som kan höja chansen." },

      { t: "h2", text: "Så ansöker du via en låneförmedlare" },
      {
        t: "steps",
        title: "Fyra steg till färdiga bud",
        items: [
          "Fyll i belopp, löptid och syfte i formuläret.",
          "Signera med BankID – en enda kreditupplysning tas.",
          "Förmedlaren skickar ansökan vidare till flera banker.",
          "Jämför buden och välj det billigaste – helt utan bindning.",
        ],
      },

      { t: "h2", text: "Medsökande sänker räntan" },
      { t: "p", text: "Med en medsökande bedöms er samlade ekonomi, vilket i regel ger både högre lånelöfte och lägre ränta. Kom ihåg att båda blir solidariskt betalningsansvariga för hela skulden." },

      { t: "h2", text: "Ny eller begagnad bil – påverkar det lånet?" },
      { t: "p", text: "Ett blancolån bryr sig inte om bilens ålder, till skillnad från bilfinansiering med säkerhet där äldre bilar ofta nekas eller ger sämre villkor. Köper du privat eller från en mindre handlare är blancolån ofta enda vägen." },
      { t: "h3", text: "Räkna på hela ägandekostnaden" },
      { t: "p", text: "Försäkring, skatt, däck, service och värdeminskning kostar ofta mer per år än räntan. En billigare bil med samma lån ger därför nästan alltid bättre ekonomi än en dyrare bil med lägre ränta." },

      { t: "h2", text: "Vanliga misstag vid bilköp på lån" },
      {
        t: "box",
        tone: "warning",
        title: "Fyra fällor att undvika",
        items: [
          "Att bara titta på månadskostnaden och inte på total lånekostnad.",
          "Att tacka ja till handlarens finansiering utan att jämföra bud.",
          "Att välja tolv års löptid för en bil du behåller i fem.",
          "Att söka hos flera banker separat och samla på sig kreditupplysningar.",
        ],
      },

      { t: "h2", text: "För- och nackdelar med billån utan kontantinsats" },
      {
        t: "proscons",
        pros: [
          "Ingen kontantinsats behövs",
          "Du äger bilen direkt och kan sälja fritt",
          "Kontantköpare ger bättre förhandlingsläge",
          "Ett bud från flera banker på en kreditupplysning",
        ],
        cons: [
          "Något högre ränta än lån med säkerhet",
          "Kräver stabil inkomst och ren kreditbakgrund",
          "Hela beloppet lånas – ingen buffert i bilen",
          "Lång löptid kan göra bilen dyrare än den är värd",
        ],
      },

      { t: "h2", text: "Har du redan dyra krediter?" },
      { t: "p", text: "Ligger räntan på befintliga krediter över tio procent gör du oftast störst nytta genom att först samla dem. Läs mer på vår sida om [samlingslån](/samla-lan), eller se [privatlån med lägst ränta](/privatlan-jamforelse-lagst-ranta-2026) om bilköpet kan vänta någon månad." },

      { t: "h2", text: "Så granskar vi långivarna" },
      { t: "p", text: "Vi väger ränta, avgifter, antal anslutna banker, transparens i villkoren, tillstånd hos Finansinspektionen och verkliga kundomdömen. Läs mer om [hur vi granskar](/om-oss/hur-vi-granskar) och om vår [redaktion](/om-oss/redaktion)." },

      { t: "h2", text: "Sammanfattning" },
      { t: "p", text: "Billån på 200 000 kr utan kontantinsats fungerar bäst som blancolån via en låneförmedlare: en kreditupplysning, flera bud och ingen kontantinsats. Välj kortast möjliga löptid, jämför effektiv ränta i stället för nominell och räkna alltid på total lånekostnad innan du skriver på." },

      { t: "h2", text: "Källor" },
      {
        t: "ul",
        items: [
          "[Finansinspektionen](https://www.fi.se) – tillståndsregister och tillsyn",
          "[Konsumentverket](https://www.konsumentverket.se) – kreditregler, ångerrätt och räkneexempel",
          "[Riksbanken](https://www.riksbank.se) – referensränta och ränteläget 2026",
        ],
      },
    ],
  },
};
